/* eslint-disable no-classname/no-classname-outside-designsystem */
import { t } from '@lingui/core/macro'
import { Button } from 'designSystem/components/Button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from 'designSystem/components/DropdownMenu'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'designSystem/components/Popover'
import { useGeckoLocale } from 'features/locale'
import { Info, MousePointer2, SlidersHorizontal } from 'lucide-react'
import type { ReactNode } from 'react'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

export type MeasurementStripExtraValue = {
  label: string
  value: GeckoMeasurement | undefined
  infoContent?: ReactNode
}

export type MeasurementStripCursorLocation = {
  x: GeckoMeasurement
  y: GeckoMeasurement
}

export type MeasurementStripProps = {
  lo: GeckoMeasurement | undefined
  hi: GeckoMeasurement | undefined
  loColor: string | undefined
  hiColor: string | undefined
  cursorValue: GeckoMeasurement | undefined
  cursorColor: string | undefined
  cursorLocation?: MeasurementStripCursorLocation
  cursorExtraValues?: readonly MeasurementStripExtraValue[]
  settingsContent?: ReactNode
  endContent?: ReactNode
  backgroundClassName?: string
  className?: string
}

const VALUE_SLOT_CLASS_NAME =
  'inline-block min-w-[8ch] shrink-0 whitespace-nowrap text-right'
const COORDINATE_VALUE_SLOT_CLASS_NAME =
  'inline-block w-[8ch] shrink-0 whitespace-nowrap text-right'
const EXTRA_VALUE_SLOT_CLASS_NAME = 'inline-block w-[4ch] shrink-0 text-right'
const MEASUREMENT_PILL_CLASS_NAME =
  'inline-flex h-7 items-center gap-1 rounded-md border border-border/70 bg-background/80 px-2 shadow-sm backdrop-blur-sm'
const MEASUREMENT_GROUP_CLASS_NAME =
  'inline-flex h-7 items-center gap-2 rounded-md border border-border/70 bg-background/80 px-2 shadow-sm backdrop-blur-sm'
const MEASUREMENT_GROUP_ITEM_CLASS_NAME = 'inline-flex items-center gap-1'

export function MeasurementStrip({
  lo,
  hi,
  loColor,
  hiColor,
  cursorValue,
  cursorColor,
  cursorLocation,
  cursorExtraValues = [],
  settingsContent,
  endContent,
  backgroundClassName = 'bg-muted',
  className = '',
}: MeasurementStripProps) {
  const { formatMeasurement } = useGeckoLocale()

  if (!lo && !hi) return null

  const formatValue = (
    measurement: GeckoMeasurement | undefined,
    options: {
      fractionDigits?: number
      fallbackUnit?: GeckoMeasurementUnit
    } = {}
  ) => {
    const fractionDigits = options.fractionDigits
    if (!measurement && options.fallbackUnit) {
      return formatFallbackValue(options.fallbackUnit, fractionDigits)
    }
    return (
      formatMeasurement({
        measurement,
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
      }) ?? '-'
    )
  }

  const formatFallbackValue = (
    fallbackUnit: GeckoMeasurementUnit,
    fractionDigits: number | undefined
  ) => {
    if (fallbackUnit === GeckoMeasurementUnit.Percent) return '-%'

    const formattedZero = formatMeasurement({
      measurement: GeckoMeasurement.CreateFromValue(0, fallbackUnit),
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    })

    return formattedZero?.replace(/^[-+]?\d+(?:\.\d+)?/, '-') ?? '-'
  }

  const formatExtraValue = (measurement: GeckoMeasurement | undefined) =>
    measurement?.unit === GeckoMeasurementUnit.Percent
      ? formatValue(measurement, { fractionDigits: 0 })
      : formatValue(measurement)

  const formatCoordinateValue = (measurement: GeckoMeasurement) =>
    formatMeasurement({
      measurement,
      variant: 'geometric',
    }) ?? '-'

  return (
    <div
      className={`flex items-center gap-2 px-4 py-2 text-[0.625rem] tabular-nums text-muted-foreground ${backgroundClassName} ${className}`}>
      <span className={MEASUREMENT_GROUP_CLASS_NAME}>
        {settingsContent ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                aria-label={t`Color scale settings`}
                className='pointer-events-auto -ml-1 size-6 shrink-0 text-muted-foreground hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground'>
                <SlidersHorizontal className='size-3.5' aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align='start'
              side='top'
              sideOffset={6}
              className='z-[1001] w-[30rem] max-w-[calc(100vw-2rem)] rounded-md border-border/70 bg-background/90 p-2 shadow-sm backdrop-blur-sm'>
              <DropdownMenuLabel className='px-1 pb-2 pt-0 text-[0.625rem] font-medium uppercase tracking-wide text-muted-foreground/80'>
                {t`Color scale`}
              </DropdownMenuLabel>
              {settingsContent}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}

        <span className={MEASUREMENT_GROUP_ITEM_CLASS_NAME}>
          <span
            className='inline-block h-3 w-0.5 rounded-full'
            style={{
              backgroundColor: loColor ?? 'currentColor',
            }}
          />
          <span className='font-medium uppercase tracking-wide text-muted-foreground/70'>
            {t`Lo`}
          </span>
          <span className='text-foreground'>{formatValue(lo)}</span>
        </span>

        <span className={MEASUREMENT_GROUP_ITEM_CLASS_NAME}>
          <span
            className='inline-block h-3 w-0.5 rounded-full'
            style={{
              backgroundColor: hiColor ?? 'currentColor',
            }}
          />
          <span className='font-medium uppercase tracking-wide text-muted-foreground/70'>
            {t`Hi`}
          </span>
          <span className='text-foreground'>{formatValue(hi)}</span>
        </span>
      </span>

      <span
        className={`${MEASUREMENT_PILL_CLASS_NAME} ml-auto shrink-0 justify-end`}>
        <span
          className='inline-block h-3 w-0.5 rounded-full'
          style={{ backgroundColor: cursorColor ?? 'currentColor' }}
        />
        <MousePointer2 className='size-3' />
        <span className='text-foreground'>
          {cursorLocation ? (
            <span className='mr-2 inline-flex shrink-0 items-center gap-1 text-muted-foreground'>
              <span className='font-medium uppercase tracking-wide text-muted-foreground/70'>
                X
              </span>
              <span
                className={`text-foreground ${COORDINATE_VALUE_SLOT_CLASS_NAME}`}>
                {formatCoordinateValue(cursorLocation.x)}
              </span>
              <span className='text-muted-foreground/50'>·</span>
              <span className='font-medium uppercase tracking-wide text-muted-foreground/70'>
                Y
              </span>
              <span
                className={`text-foreground ${COORDINATE_VALUE_SLOT_CLASS_NAME}`}>
                {formatCoordinateValue(cursorLocation.y)}
              </span>
              <span className='ml-1 h-3 w-px bg-border/70' aria-hidden />
            </span>
          ) : null}
          <span className={VALUE_SLOT_CLASS_NAME}>
            {formatValue(cursorValue, {
              fallbackUnit: lo?.unit ?? hi?.unit,
            })}
          </span>
          {cursorExtraValues.map(({ label, value, infoContent }) => (
            <span
              key={label}
              className='ml-3 inline-flex shrink-0 items-center gap-1 text-muted-foreground'>
              {label}
              {infoContent ? (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type='button'
                      variant='ghost'
                      size='icon-xs'
                      aria-label={t`${label} information`}
                      className='pointer-events-auto -ml-0.5 size-4 rounded-full text-muted-foreground/80 hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground'>
                      <Info className='size-3' aria-hidden />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    side='top'
                    align='end'
                    sideOffset={8}
                    className='pointer-events-auto w-72 gap-2 rounded-md border-border/70 bg-background/95 p-3 text-[0.6875rem] leading-relaxed shadow-sm backdrop-blur-sm'>
                    {infoContent}
                  </PopoverContent>
                </Popover>
              ) : null}{' '}
              <span
                className={`text-foreground ${EXTRA_VALUE_SLOT_CLASS_NAME}`}>
                {formatExtraValue(value)}
              </span>
            </span>
          ))}
        </span>
      </span>

      {endContent ? <span className='shrink-0'>{endContent}</span> : null}
    </div>
  )
}
